import {
  dxtToLocalServerTime,
  useUTLSettingsContext,
} from "@dexteel/mesf-core";
import { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-enterprise";
import { get } from "lodash-es";
import { CommentsColumn } from "@/controls/ag-grid/components/CommentsColumn";
import {
  BooleanYNBadgeRenderer,
  LinkCellRenderer,
  ManualIconCellRenderer,
} from "../../../../controls/ag-grid/components/ButtonCellRenderer";
import { ProcessesButtons } from "../../../../controls/buttons/ProcessesButtons";

type Props = {
  showCommentModal: boolean;
  setShowCommentModal: (
    show: boolean,
    data?: any,
    position?: { top: number; left: number },
  ) => void;
  linkData: (data: any) => void;
  unlinkData: (data: any) => void;
  toggleStatusData: (data: any) => void;
  showContextMenu: (e: React.MouseEvent, data: any, configId: string) => void;
};

export const useTableData = ({
  setShowCommentModal,
  linkData,
  unlinkData,
  toggleStatusData,
  showContextMenu,
}: Props) => {
  const { state } = useUTLSettingsContext();
  const targetTTT = parseInt(get(state.settings, "EAF_TARGET_TTT", 60));

  const columns: (ColDef | ColGroupDef)[] = [
    {
      field: "HeatName",
      headerName: "Heat",
      cellRenderer: (params: ICellRendererParams) => {
        return LinkCellRenderer({
          ...params,
          link: `/meltshop-heats/${params.data.HeatShiftId}?heatName=${params.data.HeatName}`,
        });
      },
      flex: 1,
      minWidth: 60,
    },
    {
      field: "GradeName",
      headerName: "Grade",
      flex: 1,
      minWidth: 80,
    },
    {
      field: "LadleName",
      headerName: "Ladle",
      flex: 1,
      minWidth: 40,
    },
    {
      field: "Crew",
      headerName: "Crew",
      flex: 1,
      minWidth: 40,
    },
    {
      field: "PrevTap",
      headerName: "PrevTap",
      flex: 1,
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.PrevTap, "HH:mm:ss") || "",
      minWidth: 60,
    },
    {
      field: "StartHeat",
      headerName: "Start",
      flex: 1,
      valueGetter: (params) =>
        dxtToLocalServerTime(params.data?.StartHeat, "HH:mm:ss") || "",
      minWidth: 60,
    },
    {
      headerName: "Tap",
      children: [
        {
          field: "Start",
          headerName: "Start",
          cellRenderer: (params: ICellRendererParams) => {
            return LinkCellRenderer({
              ...params,
              link: `/eaf-data-entry/${params.data.ProcessId}`,
            });
          },
          flex: 1,
          minWidth: 60,
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.Start, "HH:mm:ss") || "",
        },
        {
          field: "End",
          headerName: "End",
          flex: 1,
          minWidth: 60,
          valueGetter: (params) =>
            dxtToLocalServerTime(params.data?.End, "HH:mm:ss") || "",
        },
        {
          field: "SteelWeight",
          headerName: "Weight",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 50,
          valueGetter: (params) => params.data?.SteelWeight?.toFixed(1) || "",
        },
      ],
    },
    {
      headerName: "Times",
      children: [
        {
          field: "TapToTap",
          headerName: "TTT",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => {
            return params.data?.TapToTap?.toFixed(1) || "";
          },
          cellStyle: (params) => {
            const tttValue = params.data?.TapToTap;
            if (tttValue && tttValue > targetTTT) {
              return { color: "red" };
            }
            return null;
          },
        },
        {
          field: "PowerOn",
          headerName: "On",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.PowerOn?.toFixed(1) || "",
        },
        {
          field: "PowerOff",
          headerName: "Off",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.PowerOff?.toFixed(1) || "",
        },
      ],
    },
    {
      field: "MWH",
      headerName: "MWH",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 40,
      valueGetter: (params) => params.data?.MWH?.toFixed(1) || "",
    },
    {
      field: "KWHPerTon",
      headerName: "Kwh/t",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 40,
      valueGetter: (params) => params.data?.KWHPerTon?.toFixed(1) || "",
    },
    {
      headerName: "Scrap Buckets",
      children: [
        {
          field: "Charges",
          headerName: "#",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 30,
        },
        {
          field: "Metal",
          headerName: "Metal",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.Metal?.toFixed(0) || "",
        },
        {
          field: "LimeCharges",
          headerName: "Lime",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.LimeCharges?.toFixed(0) || "",
        },
      ],
    },
    {
      headerName: "Ladle Additions",
      children: [
        {
          field: "Lime",
          headerName: "Lime",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.Lime?.toFixed(0) || "",
        },
        {
          field: "SiMn",
          headerName: "SiMn",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.SiMn?.toFixed(0) || "",
        },
        {
          field: "FeSi",
          headerName: "FeSi",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.FeSi?.toFixed(0) || "",
        },
      ],
    },
    {
      headerName: "EAF Additions",
      children: [
        {
          field: "Coal",
          headerName: "Coal",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.Coal?.toFixed(0) || "",
        },
        {
          field: "FD",
          headerName: "FD",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.FD?.toFixed(0) || "",
        },
        {
          field: "RB",
          headerName: "RB",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.RB?.toFixed(0) || "",
        },
        {
          field: "ML",
          headerName: "ML",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.ML?.toFixed(0) || "",
        },
        {
          field: "SL",
          headerName: "SL",
          type: ["numericColumn", "numericWidth"],
          flex: 1,
          minWidth: 40,
          valueGetter: (params) => params.data?.SL?.toFixed(0) || "",
        },
      ],
    },
    {
      field: "InjectedO2",
      headerName: "O2",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.InjectedO2?.toFixed(0) || 0,
      flex: 1,
      minWidth: 50,
    },
    {
      field: "InjectedCarbon",
      headerName: "C",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.InjectedCarbon?.toFixed(0) || 0,
      flex: 1,
      minWidth: 40,
    },
    {
      field: "InjectedGas",
      headerName: "Gas",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.InjectedGas?.toFixed(0) || 0,
      flex: 1,
      minWidth: 40,
    },
    {
      field: "InjectedLime",
      headerName: "Lime Inj",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 50,
      valueGetter: (params) => params.data?.InjectedLime?.toFixed(0) || 0,
    },
    {
      field: "LastTemperature",
      headerName: "Last Temp",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 65,
      valueGetter: (params) => params.data?.LastTemperature?.toFixed(0) || "",
    },
    {
      field: "LastO2",
      headerName: "Last O2",
      type: ["numericColumn", "numericWidth"],
      flex: 1,
      minWidth: 60,
      valueGetter: (params) => params.data?.LastO2?.toFixed(0) || "",
    },
    {
      field: "LastCarbon",
      headerName: "Last C",
      type: ["numericColumn", "numericWidth"],
      valueGetter: (params) => params.data?.LastCarbon?.toFixed(3) || 0,
      flex: 1,
      minWidth: 40,
    },
    {
      field: "IsNonFreeOpen",
      headerName: "NFO",
      flex: 1,
      minWidth: 40,
      cellRenderer: BooleanYNBadgeRenderer,
      cellClass: "ag-button-cell",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
    },
    {
      field: "Comments",
      headerName: "Notes",
      flex: 1,
      minWidth: 40,
      cellRenderer: (params: ICellRendererParams) => {
        return CommentsColumn({
          data: params?.data,
          setShowOpenModal: setShowCommentModal,
        });
      },
      valueGetter: (params) => params.data?.Comments || "",
    },
    {
      field: "IsManual",
      headerName: "",
      type: ["nonSorteable", "nonFilterable", "centerAligned"],
      cellRenderer: ManualIconCellRenderer,
      cellClass: "ag-button-cell",
      flex: 1,
      minWidth: 40,
    },
    {
      cellRenderer: ProcessesButtons,
      cellRendererParams: (params: ICellRendererParams) => ({
        params: params,
        linkData: linkData,
        unlinkData: unlinkData,
        toggleStatusData: toggleStatusData,
        showContextMenu: (event: React.MouseEvent) =>
          showContextMenu(event, params.data, "eafProcessesContext"),
      }),
      wrapText: false,
      wrapHeaderText: false,
      headerName: "",
      sortable: false,
      flex: 1,
      maxWidth: 150,
      minWidth: 150,
      filter: false,
    },
  ];

  return { columns };
};
